import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { encryptId, formatDate01, formatRupiah } from '../../helper/helper';
import { formatIssuePeriod } from '../../helper/issuePeriod';
import PanelTopSecond from '../../component/Admin/PanelTopSecond';
import PageLayout from '../../components/layout/PageLayout';
import { FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import { apiHost } from '../../helper/apiHost';
import { headerImageUrl, withLegacyImageFallback } from '../../helper/mediaUrl';
import { canAccess } from '../../helper/permissions';

const emptyPaymentForm = () => ({
    paymentName: '',
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    customerAddress: '',
    amount: '',
    paymentMethodID: '',
    paymentStatus: 'pending',
});

const modules = {
    toolbar: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ direction: 'rtl' }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ['link'],
        ['clean'],
        [{ indent: '-1' }, { indent: '+1' }],
    ],
};

const LoaForm = ({ data }) => {
    const navigate = useNavigate();
    const { loaId } = useParams();
    const [searchParams] = useSearchParams();
    const isEdit = Boolean(loaId);
    const presetJournalId = searchParams.get('journal');
    const journalLocked = !isEdit && Boolean(presetJournalId);
    const presetJournalLoadedRef = useRef(null);

    const host = apiHost;
    const header = {
        headers: { Authorization: `Bearer ${data.token}` },
    };

    const [listJournal, setListJournal] = useState([]);
    const [listIssues, setListIssues] = useState([]);
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [formData, setFormData] = useState({});
    const [authors, setAuthors] = useState([]);
    const [authorInput, setAuthorInput] = useState('');
    const [affiliates, setAffiliates] = useState([]);
    const [affiliateInput, setAffiliateInput] = useState('');
    const [editorData, setEditorData] = useState('');
    const [paymentSearch, setPaymentSearch] = useState('');
    const [paymentList, setPaymentList] = useState([]);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);
    const [listPaymentMethod, setListPaymentMethod] = useState([]);
    const [paymentForm, setPaymentForm] = useState(emptyPaymentForm());
    const [linkedPayment, setLinkedPayment] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        getListJournal();
        getListPaymentMethod();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (isEdit && loaId) {
            loadLoaDetail(loaId);
        }
        // eslint-disable-next-line
    }, [loaId, isEdit]);

    useEffect(() => {
        if (isEdit || !presetJournalId) return;
        if (presetJournalLoadedRef.current === presetJournalId) return;
        presetJournalLoadedRef.current = presetJournalId;
        applyJournalTemplate(presetJournalId);
        // eslint-disable-next-line
    }, [presetJournalId, isEdit]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const paymentContainer = event.target.closest('.payment-container');
            if (!paymentContainer) {
                setShowPaymentDropdown(false);
            }
        };

        if (showPaymentDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showPaymentDropdown]);

    const getListPaymentMethod = () => {
        axios
            .get('api/payment-method', header)
            .then((response) => {
                setListPaymentMethod(response.data || []);
            })
            .catch((error) => {
                console.error('Error fetching payment methods:', error);
            });
    };

    const getListJournal = () => {
        axios
            .get('api/journal?page=1&limit=100', header)
            .then((response) => {
                setListJournal(response.data.items);
            })
            .catch((error) => {
                console.error('Error fetching data:', error);
            });
    };

    const loadLoaDetail = async (id) => {
        setLoading(true);
        try {
            const detailRes = await axios.get(`api/loa/detail/${id}`, header);
            const detail = detailRes.data;

            const authorStr = detail.author || '';
            const affiliateStr = detail.affiliate || '';
            setAuthors(authorStr ? authorStr.split(', ').filter(Boolean) : []);
            setAffiliates(affiliateStr ? affiliateStr.split(', ').filter(Boolean) : []);
            setEditorData(detail.message || '');
            setFormData(detail);

            if (detail.payment?.paymentStatus === 'lunas') {
                Swal.fire(
                    'Tidak diizinkan',
                    'LOA dengan payment lunas tidak dapat diedit.',
                    'warning'
                ).then(() => navigate(`/admin/loa/${id}`));
                return;
            }

            if (detail.payment) {
                setLinkedPayment(detail.payment);
                const payment = {
                    paymentID: detail.payment.paymentID || detail.paymentID || detail.payment_id,
                    trxID: detail.payment.trxID || '',
                    paymentName: detail.payment.paymentName || '',
                    customerName: detail.payment.customerName || '',
                    paymentStatus: detail.payment.paymentStatus || '',
                };
                setSelectedPayment(payment);
                const displayText = payment.trxID
                    ? `${payment.trxID} - ${payment.paymentName || payment.customerName || ''}`
                    : payment.paymentName || payment.customerName || '';
                setPaymentSearch(displayText);
            } else {
                const paymentID = detail.paymentID || detail.payment_id || null;
                if (paymentID) {
                    const payment = {
                        paymentID,
                        trxID: detail.paymentTrxID || detail.payment_trxID || detail.trxID || '',
                        paymentName: detail.paymentName || detail.payment_name || '',
                        customerName:
                            detail.paymentCustomerName ||
                            detail.payment_customer_name ||
                            detail.customerName ||
                            '',
                        paymentStatus: detail.paymentStatus || '',
                    };
                    setSelectedPayment(payment);
                    setLinkedPayment(payment);
                    const displayText = payment.trxID
                        ? `${payment.trxID} - ${payment.paymentName || payment.customerName || ''}`
                        : payment.paymentName || payment.customerName || '';
                    setPaymentSearch(displayText);
                } else {
                    setSelectedPayment(null);
                    setLinkedPayment(null);
                    setPaymentSearch('');
                }
            }

            const journalId = detail.journal_id?.journal_id || detail.journal_id;
            if (journalId) {
                try {
                    const issuesResponse = await axios.get(`api/issues/by-journal/${journalId}`, header);
                    const issues = issuesResponse.data || [];
                    setListIssues(issues);

                    const issuesId =
                        detail.issues_id?.issuesID || detail.issues_id || detail.issuesID;
                    if (issuesId && issues.length > 0) {
                        const selectedIssueData = issues.find(
                            (i) => i.issuesID === parseInt(issuesId, 10)
                        );
                        if (selectedIssueData) {
                            setSelectedIssue(selectedIssueData);
                            setFormData({
                                ...detail,
                                issues_id: selectedIssueData.issuesID,
                            });
                        }
                    }
                } catch (error) {
                    console.error('Error fetching issues:', error);
                    setListIssues([]);
                }
            } else {
                setListIssues([]);
                setSelectedIssue(null);
            }
        } catch (error) {
            console.error('Error loading LOA detail:', error);
            Swal.fire('Gagal', 'Tidak dapat memuat data LOA', 'error').then(() => {
                navigate('/admin/loa');
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (
            isEdit &&
            (linkedPayment?.paymentStatus === 'lunas' ||
                selectedPayment?.paymentStatus === 'lunas')
        ) {
            Swal.fire(
                'Tidak diizinkan',
                'LOA dengan payment lunas tidak dapat diedit.',
                'warning'
            );
            return;
        }

        if (!isEdit && (!selectedIssue || !formData?.issues_id)) {
            Swal.fire({
                title: 'Error',
                text: 'Please select an issue',
                icon: 'error',
                confirmButtonText: 'OK',
            });
            return;
        }

        if (!isEdit && selectedIssue) {
            if (
                selectedIssue.isFull ||
                selectedIssue.publishedCount >= selectedIssue.maxPublish
            ) {
                Swal.fire({
                    title: 'Issue Sudah Penuh',
                    text: `Issue ini sudah mencapai batas maksimal publish (${selectedIssue.publishedCount}/${selectedIssue.maxPublish}). Tidak dapat membuat LOA baru untuk issue ini.`,
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
                return;
            }
        }

        const payloadLoa = {
            title: formData?.title,
            author: authors.join(', '),
            affiliate: affiliates.join(', '),
            message: editorData,
            journal_id: formData?.journal_id?.journal_id || formData?.journal_id,
            issues_id: formData?.issues_id,
            journal_manager: formData?.journal_manager,
            journal_publish: formData?.journal_publish,
            header: formData?.header,
            issue: formData?.issue,
            volume: formData?.volume,
            month: formData?.month,
            year: formData?.year,
            language: formData?.language,
            date: formData?.date,
            payment_id: selectedPayment?.paymentID || null,
        };

        setSubmitting(true);

        if (isEdit && loaId) {
            axios
                .patch(`api/loa/${loaId}`, payloadLoa, header)
                .then(() => {
                    Swal.fire({
                        title: 'Success',
                        icon: 'success',
                        text: 'LOA Berhasil diupdate',
                        confirmButtonText: 'Tutup',
                        confirmButtonColor: '#4569ab',
                    }).then(() => {
                        navigate('/admin/loa');
                    });
                })
                .catch((error) => {
                    console.error('Error updating data:', error);
                    const errorMessage =
                        error.response?.data?.message ||
                        error.response?.data ||
                        'Terjadi kesalahan saat mengupdate LOA';
                    Swal.fire({
                        title: 'Error',
                        text: errorMessage,
                        icon: 'error',
                        confirmButtonText: 'OK',
                    });
                })
                .finally(() => setSubmitting(false));
        } else {
            if (
                !paymentForm.customerName ||
                !paymentForm.customerPhone ||
                !paymentForm.customerEmail ||
                !paymentForm.customerAddress ||
                !paymentForm.amount ||
                !paymentForm.paymentMethodID
            ) {
                Swal.fire({
                    title: 'Error',
                    text: 'Lengkapi data payment (customer, amount, metode pembayaran)',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
                setSubmitting(false);
                return;
            }

            const selectedJournalObj = listJournal.find(
                (j) => String(j.journal_id) === String(payloadLoa.journal_id)
            );

            const payload = {
                ...payloadLoa,
                payment: {
                    paymentName:
                        paymentForm.paymentName || payloadLoa.title || 'Biaya publikasi',
                    articleName: payloadLoa.title,
                    journalName: selectedJournalObj?.journal_name || '',
                    customerName: paymentForm.customerName,
                    customerPhone: paymentForm.customerPhone,
                    customerEmail: paymentForm.customerEmail,
                    customerAddress: paymentForm.customerAddress,
                    amount: String(paymentForm.amount).replace(/[^\d]/g, ''),
                    paymentMethodID: Number(paymentForm.paymentMethodID),
                    paymentStatus: paymentForm.paymentStatus || 'pending',
                },
            };

            axios
                .post('api/loa/with-payment', payload, header)
                .then(() => {
                    Swal.fire({
                        title: 'Success',
                        icon: 'success',
                        text: 'LOA & Payment berhasil ditambahkan',
                        confirmButtonText: 'Tutup',
                        confirmButtonColor: '#4569ab',
                    }).then(() => {
                        navigate('/admin/loa');
                    });
                })
                .catch((error) => {
                    console.error('Error adding data:', error);
                    const errorMessage =
                        error.response?.data?.message ||
                        error.response?.data ||
                        'Terjadi kesalahan saat menambahkan LOA';
                    Swal.fire({
                        title: 'Error',
                        text: Array.isArray(errorMessage)
                            ? errorMessage.join(', ')
                            : errorMessage,
                        icon: 'error',
                        confirmButtonText: 'OK',
                    });
                })
                .finally(() => setSubmitting(false));
        }
    };

    const applyJournalTemplate = async (selectedJournalId) => {
        if (!selectedJournalId) {
            setListIssues([]);
            setSelectedIssue(null);
            return;
        }

        try {
            const response = await axios.get(
                `api/journal/${encryptId(`${selectedJournalId}`)}`,
                header
            );
            setFormData((prev) => ({ ...prev, ...response.data }));
            setEditorData(response.data.message || '');
        } catch (error) {
            console.error('Error fetching journal:', error);
        }

        try {
            const issuesResponse = await axios.get(
                `api/issues/by-journal/${selectedJournalId}`,
                header
            );
            setListIssues(issuesResponse.data || []);
        } catch (error) {
            console.error('Error fetching issues:', error);
            setListIssues([]);
        }

        setSelectedIssue(null);
    };

    const handleTemplateChange = async (event) => {
        await applyJournalTemplate(event.target.value);
    };

    const handleIssueChange = async (event) => {
        const issueId = event.target.value;
        if (!issueId) {
            setSelectedIssue(null);
            setFormData({
                ...formData,
                issues_id: null,
                volume: '',
                issue: '',
                month: '',
                year: '',
            });
            return;
        }

        const issue = listIssues.find((i) => i.issuesID === parseInt(issueId, 10));

        if (issue) {
            const publishedCount = Number(issue.publishedCount) || 0;
            const maxPublish = Number(issue.maxPublish) || 0;
            const isFull =
                issue.isFull === true ||
                issue.isFull === 'true' ||
                publishedCount >= maxPublish;

            const currentIssuesId = formData?.issues_id?.issuesID || formData?.issues_id;
            const isCurrentIssue =
                isEdit &&
                currentIssuesId &&
                Number(currentIssuesId) === Number(issue.issuesID);

            if (isFull) {
                if (!isEdit) {
                    Swal.fire({
                        title: 'Issue Sudah Penuh',
                        text: `Issue ini sudah mencapai batas maksimal publish (${publishedCount}/${maxPublish}). Tidak dapat membuat LOA baru untuk issue ini.`,
                        icon: 'warning',
                        confirmButtonText: 'OK',
                    });
                    event.target.value = '';
                    setSelectedIssue(null);
                    setFormData({
                        ...formData,
                        issues_id: null,
                        volume: '',
                        issue: '',
                        month: '',
                        year: '',
                    });
                    return;
                } else if (!isCurrentIssue) {
                    Swal.fire({
                        title: 'Issue Sudah Penuh',
                        text: `Issue ini sudah mencapai batas maksimal publish (${publishedCount}/${maxPublish}). Tidak dapat mengubah LOA ke issue ini.`,
                        icon: 'warning',
                        confirmButtonText: 'OK',
                    });
                    event.target.value = currentIssuesId || '';
                    setSelectedIssue(
                        listIssues.find((i) => i.issuesID === Number(currentIssuesId)) || null
                    );
                    return;
                }
            }

            setSelectedIssue(issue);
            setFormData({
                ...formData,
                issues_id: issue.issuesID,
                volume: issue.volume,
                issue: issue.issue,
                month: issue.start_month || issue.month,
                year: issue.start_year || issue.year,
            });
        } else {
            setSelectedIssue(null);
        }
    };

    const handleBlur = () => {
        if (authorInput.trim() !== '') {
            setAuthors([...authors, authorInput.trim()]);
            setAuthorInput('');
        }
    };

    const handleRemoveAuthor = (index) => {
        setAuthors(authors.filter((_, i) => i !== index));
    };

    const handleBlurAffiliate = () => {
        if (affiliateInput.trim() !== '') {
            setAffiliates([...affiliates, affiliateInput.trim()]);
            setAffiliateInput('');
        }
    };

    const handleRemoveAffiliate = (index) => {
        setAffiliates(affiliates.filter((_, i) => i !== index));
    };

    const handlePaymentSearch = async (value) => {
        setPaymentSearch(value);
        if (value.trim().length > 0) {
            try {
                const response = await axios.get(
                    `api/payment?page=1&limit=10&search=${encodeURIComponent(value)}`,
                    header
                );
                setPaymentList(response.data.items || []);
                setShowPaymentDropdown(true);
            } catch (error) {
                console.error('Error searching payments:', error);
                setPaymentList([]);
            }
        } else {
            setPaymentList([]);
            setShowPaymentDropdown(false);
        }
    };

    const handleSelectPayment = (payment) => {
        const paymentLoaId = payment.loa_id;
        const currentLoaId = formData?.loa_id || Number(loaId);

        if (paymentLoaId && paymentLoaId !== currentLoaId) {
            Swal.fire({
                title: 'Payment Sudah Digunakan',
                text: 'Payment ini sudah terhubung dengan LOA lain dan tidak dapat dipilih.',
                icon: 'warning',
                confirmButtonText: 'OK',
            });
            return;
        }

        setSelectedPayment(payment);
        setLinkedPayment(payment);
        setPaymentSearch(
            `${payment.trxID} - ${payment.paymentName || payment.customerName || ''}`
        );
        setShowPaymentDropdown(false);
        setPaymentList([]);
    };

    const handleClearPayment = () => {
        setSelectedPayment(null);
        setLinkedPayment(null);
        setPaymentSearch('');
        setPaymentList([]);
        setShowPaymentDropdown(false);
    };

    const handleChangeInput = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value || '' });
    };

    const handlePaymentFormChange = (e) => {
        const { name, value } = e.target;
        setPaymentForm((prev) => ({ ...prev, [name]: value }));
    };

    const handlePaymentAmountChange = (e) => {
        const rawValue = e.target.value.replace(/[^\d]/g, '');
        setPaymentForm((prev) => ({ ...prev, amount: formatRupiah(rawValue) }));
    };

    const handleCancel = () => {
        navigate(journalLocked ? '/admin/journal' : '/admin/loa');
    };

    const journalIdForIssues = formData?.journal_id?.journal_id || formData?.journal_id;
    const isIssueDisabled = !journalIdForIssues;
    const backLink = journalLocked ? '/admin/journal' : '/admin/loa';
    const canEditPayment =
        canAccess(data?.user?.permissions || {}, 'payment', 'edit') ||
        canAccess(data?.user?.permissions || {}, 'loa', 'edit');
    const linkedPaymentTrxId = linkedPayment?.trxID || selectedPayment?.trxID || '';

    const editPaymentButton =
        isEdit && linkedPaymentTrxId && canEditPayment ? (
            <button
                type="button"
                onClick={() => navigate(`/admin/payment/${linkedPaymentTrxId}`)}
                className="btn-secondary inline-flex items-center gap-2 !min-h-9 !px-3 !text-sm"
            >
                <FaEdit className="text-xs" />
                Edit Payment
            </button>
        ) : null;

    if (loading) {
        return (
            <PageLayout>
                <PanelTopSecond link={backLink} actions={editPaymentButton} />
                <div className="card flex min-h-[320px] w-full items-center justify-center">
                    <div
                        className="h-8 w-8 animate-spin rounded-full border-2 border-hairline border-t-primary"
                        role="status"
                        aria-label="Memuat"
                    />
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <PanelTopSecond link={backLink} actions={editPaymentButton} />

            <form
                onSubmit={handleSubmit}
                className="card relative flex w-full min-h-[70vh] flex-col overflow-hidden"
            >
                <div className="flex flex-col gap-2 border-b border-hairline px-4 py-4 dark:border-white/10 md:flex-row md:items-center md:justify-between md:px-6">
                    <div>
                        <h2 className="text-lg font-semibold text-ink dark:text-white">
                            {isEdit ? 'Edit LOA' : 'Tambah LOA'}
                        </h2>
                        <p className="mt-0.5 text-sm text-muted">
                            {isEdit
                                ? 'Perbarui data letter of acceptance'
                                : 'Buat letter of acceptance baru'}
                        </p>
                    </div>
                    {isEdit && formData?.submitID ? (
                        <span className="tag font-mono text-xs">{formData.submitID}</span>
                    ) : null}
                </div>

                <div className="card-body grid w-full flex-1 grid-cols-1 gap-x-4 gap-y-4 overflow-y-auto pb-28 lg:grid-cols-4">
                    <h3 className="form-section-title lg:col-span-4">Data LOA</h3>

                    <div className="lg:col-span-4">
                        <label
                            htmlFor="select_template"
                            className="form-label"
                        >
                            Journal
                        </label>
                        {isEdit || journalLocked ? (
                            <div>
                                {(formData?.journal_id?.header || formData?.header) && (
                                    <img
                                        src={headerImageUrl(formData?.journal_id?.header || formData?.header)}
                                        onError={(e) =>
                                            withLegacyImageFallback(
                                                e,
                                                formData?.journal_id?.header || formData?.header,
                                            )
                                        }
                                        className="animate-fade-in mb-2 block h-auto w-full rounded-md object-cover object-center"
                                        alt={formData?.journal_id?.journal_name || formData?.journal_name || 'Journal Cover'}
                                    />
                                )}
                                <select
                                    disabled
                                    id="select_template"
                                    value={
                                        formData?.journal_id?.journal_id || formData?.journal_id || ''
                                    }
                                    className="input w-full disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    <option
                                        value={
                                            formData?.journal_id?.journal_id ||
                                            formData?.journal_id ||
                                            ''
                                        }
                                    >
                                        {formData?.journal_id?.journal_name ||
                                            formData?.journal_name ||
                                            'Journal'}
                                    </option>
                                </select>
                            </div>
                        ) : (
                            <select
                                id="select_template"
                                value={
                                    formData?.journal_id?.journal_id || formData?.journal_id || ''
                                }
                                className="input w-full"
                                onChange={handleTemplateChange}
                            >
                                <option value="">--Pilih Journal--</option>
                                {listJournal?.map((item, index) => (
                                    <option key={index} value={item.journal_id}>
                                        {item.journal_name}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    <div className="lg:col-span-4">
                        <label
                            htmlFor="issues_id"
                            className="form-label"
                        >
                            Issue{!isEdit && <sup className=" text-red-500">*</sup>}
                        </label>
                        <div
                            className={`grid grid-cols-1 items-start gap-3 ${
                                selectedIssue
                                    ? 'lg:grid-cols-[minmax(280px,0.8fr)_minmax(0,2fr)]'
                                    : ''
                            }`}
                        >
                            <div>
                                <select
                                    id="issues_id"
                                    name="issues_id"
                                    className="input w-full disabled:cursor-not-allowed disabled:opacity-60"
                                    required={!isEdit}
                                    value={
                                        selectedIssue?.issuesID ||
                                        formData?.issues_id?.issuesID ||
                                        formData?.issues_id ||
                                        ''
                                    }
                                    onChange={handleIssueChange}
                                    disabled={isIssueDisabled}
                                >
                                    <option value="">--Pilih Issue--</option>
                                    {listIssues.length > 0 ? (
                                        listIssues.map((issue) => {
                                            const publishedCount =
                                                Number(issue.publishedCount) || 0;
                                            const maxPublish = Number(issue.maxPublish) || 0;
                                            const isFull =
                                                issue.isFull === true ||
                                                issue.isFull === 'true' ||
                                                publishedCount >= maxPublish;
                                            const currentIssuesId =
                                                formData?.issues_id?.issuesID ||
                                                formData?.issues_id;
                                            const isCurrentIssue =
                                                isEdit &&
                                                currentIssuesId &&
                                                Number(currentIssuesId) ===
                                                    Number(issue.issuesID);
                                            const shouldDisable =
                                                isFull && (!isEdit || !isCurrentIssue);
                                            const displayText = `Vol. ${issue.volume}, Issue ${issue.issue}, ${formatIssuePeriod(issue)} (${publishedCount}/${maxPublish})${isFull ? ' - PENUH' : ''}`;

                                            return (
                                                <option
                                                    key={issue.issuesID}
                                                    value={issue.issuesID}
                                                    disabled={shouldDisable}
                                                    style={
                                                        shouldDisable
                                                            ? {
                                                                  color: '#ef4444',
                                                                  fontWeight: 'bold',
                                                                  backgroundColor: '#fee2e2',
                                                                  cursor: 'not-allowed',
                                                              }
                                                            : {}
                                                    }
                                                >
                                                    {displayText}
                                                </option>
                                            );
                                        })
                                    ) : (
                                        <option value="" disabled>
                                            --Tidak ada Issue untuk Journal ini--
                                        </option>
                                    )}
                                </select>
                                {isIssueDisabled && (
                                    <p className="mt-1 text-xs text-muted">
                                        Pilih Journal terlebih dahulu
                                    </p>
                                )}
                                {journalIdForIssues && listIssues.length === 0 && (
                                    <p className="mt-1 text-xs text-yellow-600">
                                        Belum ada Issue untuk Journal ini. Silakan buat Issue
                                        terlebih dahulu di halaman Manage Issues.
                                    </p>
                                )}
                            </div>
                            {selectedIssue && (
                                <div
                                    className={`flex min-h-[38px] flex-wrap items-center gap-x-4 gap-y-1 rounded-md border px-3 py-1.5 text-xs ${
                                        (selectedIssue.isFull ||
                                            selectedIssue.publishedCount >=
                                                selectedIssue.maxPublish) &&
                                        !isEdit
                                            ? 'border-red-200/80 bg-red-50 dark:border-red-800/50 dark:bg-red-900/20'
                                            : 'border-hairline bg-surface-soft dark:border-white/10 dark:bg-white/[0.03]'
                                    }`}
                                    title={selectedIssue.description || ''}
                                >
                                    <span className="font-bold text-primary dark:text-primary-on-dark">
                                        Detail:
                                    </span>
                                    <span className="text-muted">
                                        Vol. <b className="text-ink dark:text-white">{selectedIssue.volume || '-'}</b>
                                    </span>
                                    <span className="text-muted">
                                        Issue <b className="text-ink dark:text-white">{selectedIssue.issue || '-'}</b>
                                    </span>
                                    <span className="text-muted">
                                        <b className="text-ink dark:text-white">{formatIssuePeriod(selectedIssue) || '-'}</b>
                                    </span>
                                    <span
                                        className={`ml-auto rounded-full px-2 py-0.5 font-bold ${
                                            selectedIssue.isFull ||
                                            selectedIssue.publishedCount >=
                                                selectedIssue.maxPublish
                                                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                                : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                        }`}
                                    >
                                        {selectedIssue.publishedCount || 0}/
                                        {selectedIssue.maxPublish}
                                    </span>
                                    {(selectedIssue.isFull ||
                                        selectedIssue.publishedCount >=
                                            selectedIssue.maxPublish) &&
                                        !isEdit && (
                                            <span className="font-semibold text-red-600 dark:text-red-300">
                                                Penuh
                                            </span>
                                        )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="lg:col-span-4">
                        <label
                            htmlFor="title"
                            className="form-label"
                        >
                            Journal Title<sup className=" text-red-500">*</sup>
                        </label>
                        <input
                            id="title"
                            type="text"
                            name="title"
                            placeholder="Title"
                            className="input w-full"
                            required
                            value={formData?.title || ''}
                            onChange={handleChangeInput}
                        />
                    </div>

                    <div className="lg:col-span-4">
                        <label className="form-label">
                            Authors<sup className=" text-red-500">*</sup>
                        </label>
                        <div className="flex flex-wrap gap-2 mb-2">
                            {authors.map((author, index) => (
                                <div
                                    key={index}
                                    className="form-chip"
                                >
                                    <span>{author}</span>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveAuthor(index)}
                                        className="ml-2 text-red-500 dark:text-red-400 font-semibold hover:text-red-700 transition-colors"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                        <input
                            type="text"
                            placeholder="Ketik nama penulis dan tekan Enter"
                            className="input w-full"
                            value={authorInput}
                            onChange={(e) => setAuthorInput(e.target.value)}
                            onBlur={handleBlur}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleBlur();
                                }
                            }}
                        />
                    </div>

                    <div className="lg:col-span-4">
                        <label className="form-label">
                            Affiliates<sup className=" text-red-500">*</sup>
                        </label>
                        <div className="flex flex-wrap gap-2 mb-2">
                            {affiliates.map((affiliate, index) => (
                                <div
                                    key={index}
                                    className="form-chip"
                                >
                                    <span>{affiliate}</span>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveAffiliate(index)}
                                        className="ml-2 text-red-500 dark:text-red-400 font-semibold hover:text-red-700 transition-colors"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                        <input
                            type="text"
                            placeholder="Type affiliate and press Enter"
                            className="input w-full"
                            value={affiliateInput}
                            onChange={(e) => setAffiliateInput(e.target.value)}
                            onBlur={handleBlurAffiliate}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleBlurAffiliate();
                                }
                            }}
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="journal_manager"
                            className="form-label"
                        >
                            Journal Manager
                        </label>
                        <input
                            id="journal_manager"
                            type="text"
                            name="journal_manager"
                            placeholder="journal manager"
                            className="input w-full"
                            required
                            value={formData?.journal_manager || ''}
                            onChange={handleChangeInput}
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="journal_publish"
                            className="form-label"
                        >
                            Journal Publish
                        </label>
                        <input
                            id="journal_publish"
                            type="text"
                            name="journal_publish"
                            placeholder=" https://journal.com/index.php/YOURPATH"
                            className="input w-full"
                            required
                            value={formData?.journal_publish || ''}
                            onChange={handleChangeInput}
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="date"
                            className="form-label"
                        >
                            Date
                        </label>
                        <input
                            id="date"
                            type="date"
                            name="date"
                            placeholder="Tanggal"
                            className="input w-full"
                            required
                            defaultValue={formatDate01(formData?.date) || ''}
                            key={formData?.date || 'date-empty'}
                            onChange={handleChangeInput}
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="bahasa"
                            className="form-label"
                        >
                            Language
                        </label>
                        <select
                            name="language"
                            id="bahasa"
                            className="input w-full"
                            onChange={handleChangeInput}
                            value={formData?.language || ''}
                        >
                            <option value="">--Pilih Bahasa--</option>
                            <option value="eng">English</option>
                            <option value="ind">Indonesia</option>
                        </select>
                    </div>

                    <div className="lg:col-span-4">
                        <label
                            htmlFor="message"
                            className="form-label"
                        >
                            Message
                        </label>
                        <div className="loa-quill overflow-hidden rounded-md border border-border-strong dark:border-white/10">
                            <ReactQuill
                                theme="snow"
                                value={editorData}
                                onChange={setEditorData}
                                modules={modules}
                            />
                        </div>
                    </div>

                    <div className="border-t border-hairline pt-8 dark:border-white/10 lg:col-span-4">
                        {!isEdit ? (
                            <>
                                <h3 className="form-section-title">Data Payment</h3>
                                <p className="mb-4 text-sm text-muted">
                                    Dibuat bersamaan dengan LOA (1x submit)
                                </p>
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                    <div>
                                        <label className="form-label">
                                            Nama Transaksi
                                        </label>
                                        <input
                                            type="text"
                                            name="paymentName"
                                            placeholder="Default: judul artikel"
                                            className="input w-full"
                                            value={paymentForm.paymentName}
                                            onChange={handlePaymentFormChange}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">
                                            Nama Customer<sup className=" text-red-500">*</sup>
                                        </label>
                                        <input
                                            type="text"
                                            name="customerName"
                                            required
                                            placeholder="Nama customer"
                                            className="input w-full"
                                            value={paymentForm.customerName}
                                            onChange={handlePaymentFormChange}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">
                                            Telepon<sup className=" text-red-500">*</sup>
                                        </label>
                                        <input
                                            type="text"
                                            name="customerPhone"
                                            required
                                            placeholder="08xxxxxxxxxx"
                                            className="input w-full"
                                            value={paymentForm.customerPhone}
                                            onChange={handlePaymentFormChange}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">
                                            Email<sup className=" text-red-500">*</sup>
                                        </label>
                                        <input
                                            type="email"
                                            name="customerEmail"
                                            required
                                            placeholder="email@example.com"
                                            className="input w-full"
                                            value={paymentForm.customerEmail}
                                            onChange={handlePaymentFormChange}
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="form-label">
                                            Alamat<sup className=" text-red-500">*</sup>
                                        </label>
                                        <textarea
                                            name="customerAddress"
                                            required
                                            rows="2"
                                            placeholder="Alamat customer"
                                            className="input min-h-[72px] w-full resize-y !h-auto py-2.5"
                                            value={paymentForm.customerAddress}
                                            onChange={handlePaymentFormChange}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">
                                            Jumlah (Rp)<sup className=" text-red-500">*</sup>
                                        </label>
                                        <input
                                            type="text"
                                            name="amount"
                                            required
                                            placeholder="0"
                                            className="input w-full"
                                            value={paymentForm.amount}
                                            onChange={handlePaymentAmountChange}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">
                                            Metode Pembayaran
                                            <sup className=" text-red-500">*</sup>
                                        </label>
                                        <select
                                            name="paymentMethodID"
                                            required
                                            className="input w-full"
                                            value={paymentForm.paymentMethodID}
                                            onChange={handlePaymentFormChange}
                                        >
                                            <option value="">--Pilih Metode--</option>
                                            {listPaymentMethod.map((m) => (
                                                <option
                                                    key={m.paymentMethodID}
                                                    value={m.paymentMethodID}
                                                >
                                                    {m.bank} - {m.rekening_number} ({m.owner})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-label">
                                            Status Payment
                                        </label>
                                        <select
                                            name="paymentStatus"
                                            className="input w-full"
                                            value={paymentForm.paymentStatus}
                                            onChange={handlePaymentFormChange}
                                        >
                                            <option value="pending">Pending</option>
                                            <option value="lunas">Lunas</option>
                                        </select>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <h3 className="form-section-title">Payment</h3>
                                {linkedPayment && (
                                    <div className="mb-4 rounded-md border border-hairline bg-surface-soft p-4 dark:border-white/10 dark:bg-white/[0.03]">
                                        <p className="mb-3 text-[11px] font-medium uppercase tracking-wider text-muted">
                                            Payment Terhubung
                                        </p>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between gap-2">
                                                <span className="text-muted">Trx ID</span>
                                                <span className="tag font-mono text-xs">
                                                    {linkedPayment.trxID || '-'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between gap-2 items-center">
                                                <span className="text-muted">Status</span>
                                                <span
                                                    className={
                                                        linkedPayment.paymentStatus === 'lunas'
                                                            ? 'tag-accent'
                                                            : 'tag'
                                                    }
                                                >
                                                    {linkedPayment.paymentStatus || '-'}
                                                </span>
                                            </div>
                                            {(linkedPayment.customerName ||
                                                linkedPayment.paymentName) && (
                                                    <div className="flex justify-between gap-2">
                                                        <span className="text-muted">Customer</span>
                                                        <span className="text-ink dark:text-white">
                                                            {linkedPayment.customerName ||
                                                                linkedPayment.paymentName}
                                                        </span>
                                                    </div>
                                                )}
                                        </div>
                                        {canEditPayment && linkedPaymentTrxId ? (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    navigate(`/admin/payment/${linkedPaymentTrxId}`)
                                                }
                                                className="btn-outline-accent mt-3 inline-flex w-full items-center justify-center gap-2 !min-h-9 !text-xs"
                                            >
                                                <FaEdit className="h-3 w-3" />
                                                Edit Payment
                                            </button>
                                        ) : null}
                                    </div>
                                )}
                                <label
                                    htmlFor="payment"
                                    className="form-label"
                                >
                                    Payment (link existing)
                                </label>
                                <div className="relative payment-container">
                                    <input
                                        id="payment"
                                        type="text"
                                        placeholder="Ketik untuk mencari payment..."
                                        className="input w-full"
                                        value={paymentSearch}
                                        onChange={(e) => handlePaymentSearch(e.target.value)}
                                        onFocus={() => {
                                            if (
                                                paymentSearch.trim().length > 0 &&
                                                paymentList.length > 0
                                            ) {
                                                setShowPaymentDropdown(true);
                                            }
                                        }}
                                    />
                                    {selectedPayment && (
                                        <button
                                            type="button"
                                            onClick={handleClearPayment}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 hover:text-red-700 font-bold text-lg"
                                        >
                                            ×
                                        </button>
                                    )}
                                    {showPaymentDropdown && paymentList.length > 0 && (
                                        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-hairline bg-canvas shadow-tesla-hover dark:border-white/10 dark:bg-tile-2">
                                            {paymentList.map((payment) => {
                                                const paymentLoaId = payment.loa_id;
                                                const currentLoaId =
                                                    formData?.loa_id || Number(loaId);
                                                const paymentStatus =
                                                    payment?.paymentStatus === 'lunas';
                                                const isUsed =
                                                    paymentLoaId && paymentLoaId !== currentLoaId;
                                                const isDisabled = isUsed;

                                                return (
                                                    <div
                                                        key={payment.paymentID}
                                                        className="flex items-center justify-between border-b border-hairline transition-colors hover:bg-surface-soft dark:border-white/10 dark:hover:bg-white/[0.04]"
                                                    >
                                                        <div
                                                            onClick={() =>
                                                                !isDisabled &&
                                                                handleSelectPayment(payment)
                                                            }
                                                            className={`px-4 py-2 last:border-b-0 flex-1 ${isDisabled || !paymentStatus
                                                                    ? 'opacity-50 cursor-not-allowed'
                                                                    : 'cursor-pointer'
                                                                }`}
                                                        >
                                                            <div className="flex items-center justify-between text-sm font-medium text-ink dark:text-white">
                                                                <span>{payment.trxID}</span>
                                                            </div>
                                                            <div className="text-xs text-muted">
                                                                {payment.paymentName ||
                                                                    payment.customerName ||
                                                                    ''}{' '}
                                                                - {payment.customerEmail || ''}
                                                            </div>
                                                            {payment.amount && (
                                                                <div className="text-xs text-muted">
                                                                    Amount:{' '}
                                                                    {new Intl.NumberFormat(
                                                                        'id-ID',
                                                                        {
                                                                            style: 'currency',
                                                                            currency: 'IDR',
                                                                        }
                                                                    ).format(payment.amount)}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center justify-end pr-4">
                                                            {isDisabled && (
                                                                <span className="text-xs text-red-500 font-normal">
                                                                    (Sudah Digunakan)
                                                                </span>
                                                            )}
                                                            {!paymentStatus && (
                                                                <span className="text-xs text-red-500 font-normal">
                                                                    (Belum Lunas)
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    <input
                        id="header"
                        hidden
                        type="text"
                        name="header"
                        value={formData?.header || ''}
                        readOnly
                    />
                </div>

                <div className="flex flex-col-reverse gap-2 border-t border-hairline bg-surface-soft px-4 py-4 dark:border-white/10 dark:bg-white/[0.03] sm:flex-row sm:justify-end md:px-6">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="btn-secondary w-full sm:w-auto"
                    >
                        <FaTimes className="text-xs" />
                        Batal
                    </button>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-primary w-full disabled:opacity-50 sm:w-auto"
                    >
                        <FaSave className="text-xs" />
                        {submitting ? 'Menyimpan...' : 'Simpan'}
                    </button>
                </div>
            </form>
        </PageLayout>
    );
};

export default LoaForm;
